import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../api.js'
import { useAuth } from '../auth/AuthContext.jsx'

// User-scope participant add/edit — standalone page (project rule: every
// form has its own URL). With :id → edit, without → create.

const blank = { name: '', is_self: false, birthdate: '', phone: '', email: '', notes: '' }

export function ParticipantFormPage() {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const isEdit = Boolean(id)

  const [v, setV] = useState(blank)
  const [loading, setLoading] = useState(isEdit)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const set = (key) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setV((cur) => ({ ...cur, [key]: val }))
  }

  useEffect(() => {
    if (authLoading || !user || !isEdit) return
    api.participants.list()
      .then((d) => {
        const p = (d.participants || []).find((x) => String(x.id) === String(id))
        if (!p) { setError('not_found'); return }
        setV({
          name: p.name || '', is_self: Number(p.is_self) === 1,
          birthdate: p.birthdate || '', phone: p.phone || '',
          email: p.email || '', notes: p.notes || '',
        })
      })
      .catch((e) => setError(e.code || 'server'))
      .finally(() => setLoading(false))
  }, [authLoading, user, id, isEdit])

  async function submit(e) {
    e.preventDefault()
    setBusy(true); setError(null)
    const payload = {
      name: v.name.trim(),
      is_self: v.is_self,
      birthdate: v.birthdate || null,
      phone: v.phone || null,
      email: v.email || null,
      notes: v.notes || null,
    }
    try {
      if (isEdit) await api.participants.update(id, payload)
      else        await api.participants.create(payload)
      navigate('/settings/participants', { replace: true })
    } catch (err) {
      setError(err.code || 'server')
      setBusy(false)
    }
  }

  if (authLoading || loading) return <main className="container"><p>{t('common.loading')}</p></main>
  if (!user) return <main className="container"><p className="error">{t('auth.errors.not_authenticated')}</p></main>

  return (
    <main className="container">
      <p><Link to="/settings/participants">← {t('participants.title')}</Link></p>
      <h1>{isEdit ? t('participants.edit_title') : t('participants.add_title')}</h1>

      {error && (
        <p className="error">
          {t(`participants.errors.${error}`, { defaultValue: t(`auth.errors.${error}`, { defaultValue: error }) })}
        </p>
      )}

      <section className="card form-card">
        <form onSubmit={submit} noValidate>
          <label>
            <span>{t('participants.name')}</span>
            <input type="text" value={v.name} onChange={set('name')} required minLength={2} />
          </label>
          <label className="checkbox-label">
            <input type="checkbox" checked={!!v.is_self} onChange={set('is_self')} />
            <span>{t('participants.is_self')}</span>
          </label>
          {/* Self participant: email/birthdate/phone come from the account. */}
          {v.is_self ? (
            <p className="muted small">{t('participants.self_fields_hint')}</p>
          ) : (
            <>
              <label>
                <span>{t('participants.birthdate')}</span>
                <input type="date" value={v.birthdate} onChange={set('birthdate')} />
              </label>
              <label>
                <span>{t('participants.phone')}</span>
                <input type="tel" value={v.phone} onChange={set('phone')} />
              </label>
              <label>
                <span>{t('participants.email')}</span>
                <input type="email" value={v.email} onChange={set('email')} />
              </label>
            </>
          )}
          <label>
            <span>{t('participants.notes')}</span>
            <textarea value={v.notes} onChange={set('notes')} rows={3} />
          </label>
          <div className="form-actions">
            <button type="submit" disabled={busy}>{busy ? t('common.loading') : t('common.save')}</button>
            <Link to="/settings/participants" className="link-button">{t('common.cancel')}</Link>
          </div>
        </form>
      </section>
    </main>
  )
}
